import {
  MIN_WSKAZAN_OGOLEM,
  MIN_WSKAZAN_PARAMETRU,
  ocenaOgolna,
  ocenSlownie,
  type WynikOceny,
} from "@shared/ocenaSlowna.js";
import { ApiError, type SessionDetail } from "./api.js";
import { odmien } from "./polski.js";
import { bladZatwierdzenia, type BladZatwierdzenia } from "./podzial.js";
import {
  KLUCZ_BEZ_MOWCY,
  type CwiczenieWidoku,
  type GrupaParametru,
  type MowcaRaportu,
  type ParametrWidoku,
  type RaportOceny,
  type WskazanieWidoku,
} from "./raportOceny.js";

/**
 * Model widoku wyniku zwyklego uzytkownika (konto `user`) jako dane: czyste funkcje bez DOM i bez Reacta. Strona
 * pages/uzytkownik/WynikPage.tsx tylko RYSUJE to, co tu policzono.
 *
 * Zrodlem jest `MowcaRaportu` i `ParametrWidoku` z lib/raportOceny.ts (wskazania pogrupowane po parametrach, tylko
 * wskazania wybranego mowcy), a ocena slowna liczy sie w KODZIE z shared/ocenaSlowna.ts: udzial mocnych stron wsrod
 * wskazan, "za malo danych" przy zbyt malej liczbie wskazan. Nic tu nie zgaduje: gdy czegos nie da sie uczciwie
 * wskazac (najmocniejszy obszar przy jednym ocenionym parametrze), funkcja oddaje null, a ekran milczy.
 */

/**
 * Czy ZADEN parametr nie zostal oceniony, bo wszystkie przebiegi sie nie powiodly (ERROR, TRUNCATED, SCHEMA_INVALID,
 * BLOCKED) albo zadanie oceny padlo bez przebiegow, choc sesja skonczyla sie jako DONE. To nie jest "czysta rozmowa bez
 * uwag": ekran ma powiedziec, ze ocena sie nie udala, a nie "nie znalezlismy nic do poprawy". Parametr "w toku" nie jest
 * porazka (ocena jeszcze trwa), wiec taki stan to false.
 */
export function nicNieOceniono(parametry: readonly Pick<ParametrWidoku, "stan">[]): boolean {
  return (
    parametry.length > 0 && parametry.every((p) => p.stan.rodzaj === "nieudana" || p.stan.rodzaj === "niepoliczona")
  );
}

// ---------------------------------------------------------------------------
// Ocena: wiersze parametrow, ocena ogolna, najmocniejszy i najslabszy obszar
// ---------------------------------------------------------------------------

/**
 * Kolor plakietki oceny: wariant plakietki (components/ui/Badge.tsx) na tokenach oceny --ocena-* (styles/ocena.css).
 *
 * Decyzja autora metodyki (29.10.2026): najlepsze sa zlote, srodek zostaje bez oznaczenia, a to, co do wymiany, jest
 * szare. Grupy ocen sa te same co przy zieleni i czerwieni: "Bardzo dobrze" i "Dobrze" (dotad zielone) to zloto,
 * "Średnio" (dotad bursztynowe) to plakietka bez oznaczenia (neutralna, BD-2: bez tintu i bez kolorowego obrysu),
 * "Słabo" (dotad czerwone) to szarosc. "Za mało danych" zostaje neutralne: to brak oceny, a nie ocena.
 * Znaczenie niesie zawsze slowo obok koloru (ST-3, A-7), wiec "Średnio" i "Za mało danych" roznia sie slowem.
 */
export type TonOceny = "zloto" | "szarosc" | "neutralny";

export function tonOceny(wynik: WynikOceny): TonOceny {
  if (wynik.rodzaj !== "ocena") return "neutralny";
  switch (wynik.ocena) {
    case "bardzo-dobrze":
    case "dobrze":
      return "zloto";
    case "srednio":
      return "neutralny";
    case "slabo":
      return "szarosc";
  }
}

/** Slowo na plakietce: ocena slowna albo neutralne "Za mało danych". */
export function etykietaOceny(wynik: WynikOceny): string {
  return wynik.rodzaj === "ocena" ? wynik.etykieta : "Za mało danych";
}

export interface ParametrUzytkownika {
  slug: string;
  /** Tytul po polsku (z wersji metodyki, ktora policzyla przebieg). */
  tytul: string;
  /** Mocne strony TEGO mowcy w tym parametrze. */
  mocne: number;
  /** Rzeczy do poprawy TEGO mowcy w tym parametrze. */
  doPoprawy: number;
  /**
   * Ocena slowna albo "za-malo-danych". null: parametru nie ma czym ocenic (przebieg nieudany, niepoliczony albo
   * jeszcze sie liczy) - ekran pokazuje wtedy `brak`, a nie wynik.
   */
  wynik: WynikOceny | null;
  /** Dlaczego nie ma wyniku (tylko gdy `wynik === null`). */
  brak: "w-toku" | "nie-udalo-sie" | null;
}

/** Udzial mocnych stron: porownanie dwoch parametrow na liczbach calkowitych (jak w shared/ocenaSlowna.ts). */
function porownajUdzial(a: ParametrUzytkownika, b: ParametrUzytkownika): number {
  return a.mocne * (b.mocne + b.doPoprawy) - b.mocne * (a.mocne + a.doPoprawy);
}

export interface WidokOcenyUzytkownika {
  /** Wszystkie parametry oceny w kolejnosci metodyki (a nie: w kolejnosci liczby wskazan). */
  parametry: ParametrUzytkownika[];
  ogolna: WynikOceny;
  /** Parametr o najwyzszym udziale mocnych stron. null: nie da sie uczciwie wskazac (patrz zbudujWidokOceny). */
  najmocniejszy: ParametrUzytkownika | null;
  /**
   * Parametr z NAJWIEKSZA liczba uwag do poprawy ("Najwięcej do poprawy: ... (N uwag)" na karcie oceny, zgodnie z zatwierdzona
   * makieta). To nie to samo, co parametr o najnizszym udziale mocnych stron: ten moze miec mniej uwag niz inny, wiec
   * karta zaprzeczalaby wlasnym licznikom. null: nie da sie uczciwie wskazac (patrz zbudujWidokOceny).
   */
  najwiecejDoPoprawy: ParametrUzytkownika | null;
  /** True, gdy ktoregos parametru nie udalo sie ocenic: ocena ogolna jest wtedy niepelna i ekran mowi o tym wprost. */
  niepelna: boolean;
}

function liczbaWskazan(grupy: readonly GrupaParametru[], slug: string): number {
  return grupy.find((g) => g.slug === slug)?.wskazania.length ?? 0;
}

/**
 * Ocena mowcy: wiersz kazdego parametru z licznikami i ocena slowna, ocena ogolna, najmocniejszy i najslabszy obszar.
 *
 * Najmocniejszy obszar i "najwiecej do poprawy" wskazujemy tylko wtedy, gdy da sie to zrobic uczciwie:
 *  - co najmniej DWA parametry maja ocene slowna (nie "za malo danych"), bo jeden nie ma z czym sie porownac,
 *  - najmocniejszy: najwyzszy udzial mocnych stron, o ile udzialy nie sa wszedzie takie same (inaczej wskazalibysmy
 *    pierwszy parametr bez powodu); remis rozstrzyga kolejnosc metodyki (pierwszy z rownych),
 *  - najwiecej do poprawy: najwieksza LICZBA uwag do poprawy (tak jak podpis "(N uwag)" na karcie), o ile jest wieksza
 *    od zera i ocenione parametry nie sa wszystkie takie same (liczba uwag i udzial); remis w liczbie uwag rozstrzyga
 *    nizszy udzial mocnych stron, a potem kolejnosc metodyki.
 */
export function zbudujWidokOceny(mowca: MowcaRaportu, parametry: readonly ParametrWidoku[]): WidokOcenyUzytkownika {
  const wiersze: ParametrUzytkownika[] = parametry.map((p) => {
    const mocne = liczbaWskazan(mowca.mocneStrony, p.slug);
    const doPoprawy = liczbaWskazan(mowca.doPoprawy, p.slug);

    if (p.stan.rodzaj === "w-toku") {
      return { slug: p.slug, tytul: p.tytul, mocne: 0, doPoprawy: 0, wynik: null, brak: "w-toku" };
    }
    if (p.stan.rodzaj === "nieudana" || p.stan.rodzaj === "niepoliczona") {
      return { slug: p.slug, tytul: p.tytul, mocne: 0, doPoprawy: 0, wynik: null, brak: "nie-udalo-sie" };
    }
    return { slug: p.slug, tytul: p.tytul, mocne, doPoprawy, wynik: ocenSlownie(mocne, doPoprawy), brak: null };
  });

  const ocenione = wiersze.filter((w) => w.wynik?.rodzaj === "ocena");
  let najmocniejszy: ParametrUzytkownika | null = null;
  let najwiecejDoPoprawy: ParametrUzytkownika | null = null;
  if (ocenione.length >= 2) {
    let najslabszyUdzial = ocenione[0];
    najmocniejszy = ocenione[0];
    for (const w of ocenione) {
      if (porownajUdzial(w, najmocniejszy) > 0) najmocniejszy = w;
      if (porownajUdzial(w, najslabszyUdzial) < 0) najslabszyUdzial = w;
    }
    // Wszystkie udzialy takie same: "najmocniejszy" byl by pierwszym z rownych bez zadnego powodu.
    if (porownajUdzial(najmocniejszy, najslabszyUdzial) === 0) najmocniejszy = null;

    let najwiecej = ocenione[0];
    for (const w of ocenione) {
      if (w.doPoprawy > najwiecej.doPoprawy || (w.doPoprawy === najwiecej.doPoprawy && porownajUdzial(w, najwiecej) < 0)) {
        najwiecej = w;
      }
    }
    // Nic nie wyroznia sie, gdy nie ma uwag w ogole albo wszystkie ocenione parametry sa pod kazdym wzgledem takie same.
    const wszystkieTakieSame = ocenione.every(
      (w) => w.doPoprawy === najwiecej.doPoprawy && porownajUdzial(w, najwiecej) === 0,
    );
    if (najwiecej.doPoprawy > 0 && !wszystkieTakieSame) najwiecejDoPoprawy = najwiecej;
  }

  return {
    parametry: wiersze,
    ogolna: ocenaOgolna(wiersze),
    najmocniejszy,
    najwiecejDoPoprawy,
    niepelna: wiersze.some((w) => w.brak === "nie-udalo-sie"),
  };
}

/**
 * Zdania pod ocena, gdy ocena jest niepelna albo zadanie oceny nie skonczylo sie czysto. Bez tresci bledu i bez
 * wzmianki o ponownej ocenie (uzytkownik jej nie ma): sam fakt, ze ocena jest niepelna.
 */
export function uwagiDoOceny(widok: Pick<WidokOcenyUzytkownika, "niepelna">, raport: Pick<RaportOceny, "uwagaZadania">): string[] {
  const uwagi: string[] = [];
  if (widok.niepelna) uwagi.push("Nie wszystkie obszary udało się ocenić, więc ocena ogólna jest niepełna.");
  if (raport.uwagaZadania) {
    uwagi.push(
      raport.uwagaZadania.rodzaj === "porazka"
        ? "Ocena nie została dokończona."
        : "Nie wszystkie wyniki oceny udało się zapisać.",
    );
  }
  return uwagi;
}

/**
 * Powod "Za mało danych" w wierszu parametru, widoczny obok plakietki ("2 z 3 potrzebnych uwag."). Krotszy niz
 * `opisZaMaloDanych` z kontraktu (ten stoi pod ocena ogolna), bo plakietka obok juz mowi "Za mało danych".
 */
export function powodZaMaloDanychParametru(wynik: Extract<WynikOceny, { rodzaj: "za-malo-danych" }>): string {
  const razem = wynik.mocne + wynik.doPoprawy;
  return razem === 0
    ? `Nie znaleźliśmy tu żadnej uwagi (potrzeba co najmniej ${wynik.potrzeba}).`
    : `${razem} z ${wynik.potrzeba} potrzebnych uwag.`;
}

/** Stopka karty oceny: skad bierze sie ocena i kiedy mowimy "Za mało danych". Reguła robocza, nie metodyka. */
export function stopkaOceny(): string {
  return (
    "Ocena wynika z tego, jaką część uwag do Twoich wypowiedzi stanowią mocne strony. To reguła robocza, którą " +
    `uzgadniamy z autorem metodyki. Przy mniej niż ${MIN_WSKAZAN_PARAMETRU} uwagach w obszarze (${MIN_WSKAZAN_OGOLEM} w całej rozmowie) ` +
    "piszemy „Za mało danych”, zamiast zgadywać."
  );
}

// ---------------------------------------------------------------------------
// Wskazania: "Co wyszlo najlepiej" i "Co warto poprawic"
// ---------------------------------------------------------------------------

/** Ta sama notka co przy wskazaniu w karcie administratora (components/ocena/Wskazania.tsx): pilnuje tego test. */
export const NOTKA_NIEZWERYFIKOWANEGO_CYTATU =
  "Nie znaleźliśmy tego cytatu w wypowiedzi słowo w słowo, więc nie zaznaczamy go w transkrypcie.";

/** Ile wskazan kazdej sekcji pokazujemy domyslnie. Reszte odslania "Pokaż wszystkie". */
export const DOMYSLNA_LICZBA_WSKAZAN = 3;

export interface ZnaleziskoUzytkownika {
  wskazanie: WskazanieWidoku;
  slug: string;
  /** Tytul parametru po polsku. */
  parametr: string;
}

/** Wskazania wszystkich grup w jednej liscie: w kolejnosci parametrow metodyki, a w parametrze w kolejnosci z oceny. */
export function splaszczWskazania(grupy: readonly GrupaParametru[]): ZnaleziskoUzytkownika[] {
  return grupy.flatMap((g) => g.wskazania.map((wskazanie) => ({ wskazanie, slug: g.slug, parametr: g.tytul })));
}

export interface WyborWskazan<T> {
  widoczne: T[];
  /** Ile wskazan jest razem. */
  razem: number;
  /** Ile jest ukrytych za "Pokaż wszystkie". */
  ukryte: number;
}

/** Pierwsze `ile` wskazan, a po rozwinieciu wszystkie. `ile` ponizej 0 liczy sie jak 0. */
export function wybierzPierwsze<T>(lista: readonly T[], ile: number, wszystkie: boolean): WyborWskazan<T> {
  const granica = wszystkie ? lista.length : Math.max(0, Math.trunc(ile));
  const widoczne = lista.slice(0, granica);
  return { widoczne, razem: lista.length, ukryte: lista.length - widoczne.length };
}

/** Pierwszy lepszy wariant wypowiedzi z oceny ("Lepiej powiedzieć"). null: model go nie podal albo jest pusty. */
export function pierwszyWariant(wskazanie: Pick<WskazanieWidoku, "warianty">): string | null {
  const tekst = wskazanie.warianty[0]?.tekst.trim();
  return tekst ? tekst : null;
}

// ---------------------------------------------------------------------------
// Cwiczenie dla uzytkownika
// ---------------------------------------------------------------------------

export interface CwiczenieUzytkownika {
  cwiczenie: CwiczenieWidoku;
  /**
   * Numer cwiczenia w `cwiczenia` parametru w metodyce, od 0. Zawsze 0: API przekazuje pod raportem wylacznie PIERWSZE
   * cwiczenie parametru (src/services/ocena/odczytSesji.ts, cwiczenieDlaPrzebiegu), a jego indeksu nie niesie w odpowiedzi.
   */
  indeks: number;
  /** Czy to cwiczenie dla parametru wskazanego jako "Najwięcej do poprawy" (wtedy ekran moze to powiedziec). */
  dlaObszaruDoPoprawy: boolean;
}

/**
 * Cwiczenie z karty "Ćwiczenie dla Ciebie". Najpierw to z parametru, w ktorym jest najwiecej do poprawy (jesli da sie
 * go uczciwie wskazac i ma cwiczenie), a gdy nie - pierwsze cwiczenie mowcy. null: mowca nie ma zadnego cwiczenia
 * (cwiczenia dostaja tylko parametry z rzeczami do poprawy).
 */
export function wybierzCwiczenie(
  mowca: Pick<MowcaRaportu, "cwiczenia">,
  obszarDoPoprawy: Pick<ParametrUzytkownika, "slug"> | null,
): CwiczenieUzytkownika | null {
  const dlaObszaru = obszarDoPoprawy ? mowca.cwiczenia.find((c) => c.slug === obszarDoPoprawy.slug) : undefined;
  const wybrane = dlaObszaru ?? mowca.cwiczenia[0];
  if (!wybrane) return null;
  return { cwiczenie: wybrane, indeks: 0, dlaObszaruDoPoprawy: dlaObszaru !== undefined };
}

/**
 * Adres treningu: /cwiczenia?parametr=<slug>&cwiczenie=<indeks>&zrodlo=<id analizy>. Strona treningu (CwiczeniaPage)
 * czyta te trzy parametry i zaklada cwiczenie bez klikania w liste.
 */
export function adresCwiczenia(slug: string, indeks: number, zrodloAnalizyId: string): string {
  const q = new URLSearchParams({ parametr: slug, cwiczenie: String(indeks), zrodlo: zrodloAnalizyId });
  return `/cwiczenia?${q.toString()}`;
}

// ---------------------------------------------------------------------------
// "Kim jestes w tej rozmowie": ustalenie mowcy uzytkownika
// ---------------------------------------------------------------------------

export type UstalenieMowcy =
  /** Wybor zapisany w sesji (`userSpeakerId`). */
  | { rodzaj: "wybrany"; klucz: string }
  /** Brak wyboru, ale tylko jeden mowca mowil: to musi byc on. */
  | { rodzaj: "jedyny"; klucz: string }
  /** Brak wyboru i kilku mowcow: trzeba zapytac. */
  | { rodzaj: "do-wyboru" }
  /** Nikt nie mowil: nie ma o kim pokazac wyniku. */
  | { rodzaj: "brak" };

/** Mowcy, o ktorych w ogole da sie pokazac wynik: ci, ktorzy mowili (bez wskazan przypietych do skasowanej wypowiedzi). */
export function mowcyDoWyboru(mowcy: readonly MowcaRaportu[]): MowcaRaportu[] {
  return mowcy.filter((m) => m.klucz !== KLUCZ_BEZ_MOWCY && m.liczbaWypowiedzi > 0);
}

/**
 * Ktorego mowcy dotyczy wynik. Regula: `userSpeakerId` z sesji (o ile to mowca tej analizy), a gdy brak i dokladnie
 * jeden mowca mowil - ten jeden; w innym razie kilku kandydatow (pytamy) albo zaden (nie ma o kim). Wybor zapisany w
 * sesji, ktorego mowcy juz nie ma (zmieniona analiza), liczy sie jak brak wyboru.
 */
export function ustalMowceUzytkownika(
  userSpeakerId: string | null | undefined,
  mowcy: readonly MowcaRaportu[],
): UstalenieMowcy {
  if (userSpeakerId && mowcy.some((m) => m.klucz === userSpeakerId && m.klucz !== KLUCZ_BEZ_MOWCY)) {
    return { rodzaj: "wybrany", klucz: userSpeakerId };
  }
  const kandydaci = mowcyDoWyboru(mowcy);
  if (kandydaci.length === 1) return { rodzaj: "jedyny", klucz: kandydaci[0].klucz };
  if (kandydaci.length === 0) return { rodzaj: "brak" };
  return { rodzaj: "do-wyboru" };
}

// ---------------------------------------------------------------------------
// Analiza w toku: trzy proste kroki zamiast etapow technicznych
// ---------------------------------------------------------------------------

export type StanKrokuWToku = "zrobione" | "w-toku" | "oczekuje";

export interface KrokWToku {
  id: "rozmowa" | "ocena" | "wynik";
  tekst: string;
  stan: StanKrokuWToku;
}

const TEKSTY_KROKOW: Readonly<Record<KrokWToku["id"], string>> = {
  rozmowa: "Czytamy rozmowę i rozpoznajemy mówców",
  ocena: "Oceniamy Twoją rozmowę",
  wynik: "Składamy wynik",
};

/**
 * Trzy kroki z jednego statusu sesji, bez nazw etapow technicznych (rozpoznawanie mowy, pomiar, brzmienie, metodyka):
 * wszystko przed ocena to krok pierwszy, ocena (LENSES) to drugi, a trzeci czeka, az wynik bedzie gotowy (wtedy
 * ekran przechodzi na wynik, wiec trzeci krok nigdy nie jest "w toku"). Status spoza toku: wszystko zrobione.
 */
export function krokiWToku(status: string): KrokWToku[] {
  const gotowe = status === "DONE" || status === "NO_SPEECH";
  const biezacy: KrokWToku["id"] | null = gotowe ? null : status === "LENSES" ? "ocena" : "rozmowa";
  const kolejnosc: KrokWToku["id"][] = ["rozmowa", "ocena", "wynik"];
  const indeksBiezacego = biezacy === null ? kolejnosc.length : kolejnosc.indexOf(biezacy);

  return kolejnosc.map((id, i) => ({
    id,
    tekst: TEKSTY_KROKOW[id],
    stan: i < indeksBiezacego ? "zrobione" : i === indeksBiezacego ? "w-toku" : "oczekuje",
  }));
}

// ---------------------------------------------------------------------------
// Tinty mowcow i opis rozmowy
// ---------------------------------------------------------------------------

/** Ile wypowiedzi dostaje kazdy z czterech tintow tozsamosci (--avatar-1..4). */
export const LICZBA_TINTOW = 4;

type SesjaDoTintow = Pick<SessionDetail, "speakers" | "utterances">;

/** Tint (1-4) po nazwie mowcy: transkrypt zna tylko nazwy. Kolejnosc nadaja mowcy sesji, wiec nazwa nie tasuje kolorow. */
export function tintDlaNazwy(sesja: SesjaDoTintow): (nazwa: string) => number {
  const mapa = new Map<string, number>();
  const dodaj = (nazwa: string): void => {
    if (!mapa.has(nazwa)) mapa.set(nazwa, (mapa.size % LICZBA_TINTOW) + 1);
  };
  sesja.speakers.forEach((s) => dodaj(s.displayName ?? s.label));
  sesja.utterances.forEach((u) => dodaj(u.speaker));
  return (nazwa) => mapa.get(nazwa) ?? 1;
}

/** Tint (1-4) po id mowcy: ekran zatwierdzania podzialu zmienia nazwy w locie, a kolory maja zostac. */
export function tintDlaIdMowcy(sesja: Pick<SessionDetail, "speakers">): (idMowcy: string) => number {
  const mapa = new Map<string, number>();
  sesja.speakers.forEach((s, i) => mapa.set(s.id, (i % LICZBA_TINTOW) + 1));
  return (idMowcy) => mapa.get(idMowcy) ?? 1;
}

/** Druga linia pod tytulem wyniku: "Rozmowa tekstowa · 357 wypowiedzi · Ty: Mateusz J.". Elementow bez danych nie ma. */
export function opisRozmowy(
  rodzaj: SessionDetail["inputKind"],
  liczbaWypowiedzi: number,
  nazwaUzytkownika: string | null,
): string {
  return [
    rodzaj === "TEXT" ? "Rozmowa tekstowa" : "Nagranie",
    liczbaWypowiedzi > 0 ? `${liczbaWypowiedzi} ${odmien(liczbaWypowiedzi, "wypowiedź", "wypowiedzi", "wypowiedzi")}` : null,
    nazwaUzytkownika ? `Ty: ${nazwaUzytkownika}` : null,
  ]
    .filter((c): c is string => c !== null)
    .join(" · ");
}

// ---------------------------------------------------------------------------
// Zatwierdzanie podzialu tekstu: bledy bez szczegolow technicznych
// ---------------------------------------------------------------------------

/**
 * Blad zatwierdzenia podzialu (POST /podzial/zatwierdz) w zdaniu dla uzytkownika. Baza to ta sama funkcja co w widoku
 * administratora (lib/podzial.ts), ale bez tego, czego uzytkownik widziec nie ma: kodow bledow w nawiasach, wzmianki o
 * modelu oceny i komunikatow o wyborze parametrow (uzytkownik niczego nie wybiera, wiec taki blad pokazujemy jako ogolny).
 * Flagi `odswiezSesje` i `odswiezParametry` zostaja bez zmian: ekran nadal pobiera dane jeszcze raz.
 */
export function bladPodzialuDlaUzytkownika(err: unknown): BladZatwierdzenia {
  const blad = bladZatwierdzenia(err);
  const kod = err instanceof ApiError ? err.code : null;

  // Jego zakladka to "Moje rozmowy", a "serwer" i "analiza" to nie jego slowa.
  if (kod === "nie_ma_takiej_sesji") {
    return { ...blad, tekst: "Nie ma już tej rozmowy. Wróć do listy rozmów." };
  }

  if (kod === "model_niedostepny") {
    return { ...blad, tekst: "Nie możemy teraz ocenić tekstu. Spróbuj ponownie później albo daj nam znać." };
  }
  if (blad.pole === "soczewki") {
    return { ...blad, pole: "ogolny", tekst: "Lista ocenianych obszarów się zmieniła. Spróbuj zatwierdzić podział jeszcze raz." };
  }
  return { ...blad, tekst: blad.tekst.replace(/\s*\([a-z0-9_]+\)/gi, "") };
}
